import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiTrash2, FiCalendar, FiEdit2, FiSave, FiX, FiUpload, FiSettings } from 'react-icons/fi';
import '../styles/PubliciteDetail.css';
import { API_BASE_URL, getTenantId } from '../config/api';
import { useServiceCategories } from '../hooks/useServices';

const PubliciteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    emplacement: 'Accueil',
    visual_url: '',
    short_title: '',
    short_description: '',
    amount_to_pay: '',
    payment_status: 'pending',
    status: 'en_attente'
  });

  const API_URL = `${API_BASE_URL}/publicites`;
  const TENANT_ID = getTenantId();
  const { categories: serviceCategories } = useServiceCategories();

  // Mapping des icônes par défaut pour les services
  const serviceIcons = {
    'Transport': '🚗',
    'Livraison': '📦',
    'Location': '🏗️',
    'Location BTP': '🏗️',
    'Conducteur': '👷',
    'default': '📋'
  };
  
  // Fonction utilitaire pour formater les dates du backend vers le format input
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    
    try {
      // Si la date est au format français DD/MM/YYYY, convertir en format ISO
      if (dateString.includes('/')) {
        const [day, month, year] = dateString.split('/');
        const date = new Date(year, month - 1, day);
        if (!isNaN(date.getTime())) {
          return date.toISOString().split('T')[0];
        }
      }
      
      // Si c'est déjà au format ISO ou autre format valide
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
    } catch (error) {
      console.error('Erreur formatage date pour input:', error);
    }
    
    return '';
  };
  
  // Fonction utilitaire pour formater les dates vers l'API
  const formatDateForAPI = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toISOString();
      }
    } catch (error) {
      console.error('Erreur formatage date pour API:', error);
    }
    return null;
  };

  useEffect(() => {
    fetchCampaign();
  }, [id]);

  const fetchCampaign = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-tenant-id': TENANT_ID,
        },
      });

      if (!response.ok) {
        throw new Error('Erreur lors du chargement de la campagne');
      }

      const data = await response.json();
      setCampaign(data);
      
      // Initialize form data with current values
      
      setFormData({
        start_date: formatDateForInput(data.start_date) || '',
        end_date: formatDateForInput(data.end_date) || '',
        emplacement: data.emplacement || 'Accueil',
        visual_url: data.visual_url || '',
        short_title: data.short_title || '',
        short_description: data.short_description || '',
        amount_to_pay: data.amount_to_pay || '',
        payment_status: data.payment_status || 'pending',
        status: data.status || 'en_attente'
      });
    } catch (err) {
      console.error('Erreur de chargement :', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/publicite');
  };

  const handleDelete = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette campagne ?')) {
      try {
        const response = await fetch(`${API_URL}/${id}`, {
          method: 'DELETE',
          headers: {
            'x-tenant-id': TENANT_ID,
          },
        });
        if (response.ok) {
          navigate('/publicite');
        }
      } catch (err) {
        console.error('Erreur suppression :', err);
      }
    }
  };

  const handleReject = async () => {
    const reason = prompt('Motif du rejet :');
    if (reason) {
      await updateStatus('rejeté', reason);
    }
  };

  const handleSuspend = async () => {
    const reason = prompt('Motif de suspension :');
    if (reason) {
      await updateStatus('suspendu', reason);
    }
  };

  const handleAccept = async () => {
    await updateStatus('accepté');
  };

  const handleReactivate = async () => {
    await updateStatus('en_cours');
  };

  const updateStatus = async (status, reason = null) => {
    try {
      const response = await fetch(`${API_URL}/${id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-tenant-id': TENANT_ID,
        },
        body: JSON.stringify({ status, reason }),
      });
      if (response.ok) {
        fetchCampaign();
      }
    } catch (err) {
      console.error('Erreur mise à jour statut :', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Simuler l'upload (dans la vraie implémentation, on uploadera le fichier)
      const fileName = `uploaded-${Date.now()}-${file.name}`;
      setFormData(prev => ({
        ...prev,
        visual_url: `/uploads/visuals/${fileName}`
      }));
    }
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Reset form data to current campaign values if canceling
      
      setFormData({
        start_date: formatDateForInput(campaign.start_date) || '',
        end_date: formatDateForInput(campaign.end_date) || '',
        emplacement: campaign.emplacement || 'Accueil',
        visual_url: campaign.visual_url || '',
        short_title: campaign.short_title || '',
        short_description: campaign.short_description || '',
        amount_to_pay: campaign.amount_to_pay || '',
        payment_status: campaign.payment_status || 'pending',
        status: campaign.status || 'en_attente'
      });
    }
    setIsEditing(!isEditing);
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    setError(null);
    
    try {
      
      const dataToSend = {
        ...formData,
        // Convert dates to ISO format for backend
        start_date: formatDateForAPI(formData.start_date),
        end_date: formatDateForAPI(formData.end_date),
      };
      
      const response = await fetch(`${API_URL}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-tenant-id': TENANT_ID,
        },
        body: JSON.stringify(dataToSend),
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors de la sauvegarde');
      }
      
      setIsEditing(false);
      fetchCampaign(); // Reload data to get updated values
    } catch (err) {
      console.error('Erreur sauvegarde :', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusAction = (newStatus) => {
    if (newStatus === 'suspendu') {
      const reason = window.prompt(`Motif de suspension pour la publicité de ${campaign.business_name}:`);
      if (reason && reason.trim()) {
        updateStatus(newStatus, reason);
      }
    } else if (newStatus === 'rejeté') {
      const reason = window.prompt(`Motif de rejet pour la publicité de ${campaign.business_name}:`);
      if (reason && reason.trim()) {
        updateStatus(newStatus, reason);
      }
    } else {
      updateStatus(newStatus);
    }
  };

  const getStatusActionButtons = () => {
    if (!campaign) return null;

    switch (campaign.status) {
      case 'en_attente':
        return (
          <>
            <button className="status-btn status-accept" onClick={() => handleStatusAction('accepté')}>
              Accepter
            </button>
            <button className="status-btn status-reject" onClick={() => handleStatusAction('rejeté')}>
              Rejeter
            </button>
          </>
        );
      case 'en_cours':
      case 'accepté':
        return (
          <>
            <button className="status-btn status-suspend" onClick={() => handleStatusAction('suspendu')}>
              Suspendre
            </button>
            <button className="status-btn status-complete" onClick={() => handleStatusAction('terminé')}>
              Terminer
            </button>
          </>
        );
      case 'suspendu':
        return (
          <button className="status-btn status-reactivate" onClick={() => handleStatusAction('en_cours')}>
            Réactiver
          </button>
        );
      case 'rejeté':
        return (
          <>
            <button className="status-btn status-accept" onClick={() => handleStatusAction('accepté')}>
              Accepter
            </button>
            <button className="status-btn status-reactivate" onClick={() => handleStatusAction('en_cours')}>
              Réactiver
            </button>
          </>
        );
      case 'terminé':
        return (
          <button className="status-btn status-reactivate" onClick={() => handleStatusAction('en_cours')}>
            Réactiver
          </button>
        );
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'en_attente': { label: 'En attente', class: 'status-pending' },
      'en_cours': { label: 'En cours', class: 'status-active' },
      'suspendu': { label: 'Suspendu', class: 'status-suspended' },
      'rejeté': { label: 'Rejeter', class: 'status-rejected' },
      'accepté': { label: 'Accepter', class: 'status-accepted' }
    };

    const config = statusConfig[status] || { label: status, class: 'status-default' };
    return <span className={`status-badge ${config.class}`}>{config.label}</span>;
  };

  const getActionButtons = () => {
    if (!campaign) return null;

    switch (campaign.status) {
      case 'en_attente':
        return (
          <div className="action-buttons">
            <button className="btn-reject" onClick={handleReject}>
              Rejeter
            </button>
            <button className="btn-accept" onClick={handleAccept}>
              Accepter
            </button>
          </div>
        );
      case 'en_cours':
        return (
          <button className="btn-suspend" onClick={handleSuspend}>
            Suspendre
          </button>
        );
      case 'suspendu':
        return (
          <button className="btn-reactivate" onClick={handleReactivate}>
            Réactiver
          </button>
        );
      case 'rejeté':
        return (
          <div className="action-buttons">
            <button className="btn-reject" onClick={handleReject}>
              Rejeter
            </button>
            <button className="btn-reactivate" onClick={handleReactivate}>
              Réactiver
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return <div className="loading">Chargement...</div>;
  }

  if (error) {
    return <div className="error">Erreur : {error}</div>;
  }

  if (!campaign) {
    return <div className="error">Campagne non trouvée</div>;
  }

  // Rendu du mobile-preview selon l'emplacement
  const renderMobilePreviewByEmplacement = () => {
    const emplacement = formData.emplacement || campaign?.emplacement || 'Accueil';
    
    switch (emplacement) {
      case 'Recherche':
        return renderSearchPreview();
      case 'Pop up':
        return renderPopupPreview();
      case 'Accueil':
      default:
        return renderHomePreview();
    }
  };

  // Vue Accueil (actuelle - ne pas changer)
  const renderHomePreview = () => (
    <div className="mobile-frame">
      <div className="mobile-header">
        <div className="mobile-status-bar">
          <div className="carrier">WAAGO</div>
          <div className="time">9:41</div>
          <div className="battery">100%</div>
        </div>
        <div className="mobile-search">
          <input type="text" placeholder="Rechercher un prestataire..." />
        </div>
      </div>
      
      <div className="mobile-content">
        <div className="mobile-wallet">
          <div className="wallet-info">
            <span>Mon Porte-feuille</span>
            <span className="wallet-amount">26.887,15 CFA</span>
          </div>
          <div className="wallet-actions">
            <button>Recharger</button>
            <button>Transférer</button>
          </div>
        </div>

        <div className="mobile-services">
          <h3>Nos services</h3>
          <div className="service-grid">
            {serviceCategories && serviceCategories.length > 0 ? (
              serviceCategories.slice(0, 4).map((cat, index) => {
                const serviceName = cat.name || cat;
                return (
                  <div className="service-item" key={cat.id || index}>
                    <div className="service-icon">{serviceIcons[serviceName] || serviceIcons.default}</div>
                    <span>{serviceName}</span>
                  </div>
                );
              })
            ) : (
              <>
                <div className="service-item">
                  <div className="service-icon">🚗</div>
                  <span>Transport</span>
                </div>
                <div className="service-item">
                  <div className="service-icon">📦</div>
                  <span>Livraison</span>
                </div>
                <div className="service-item">
                  <div className="service-icon">🏗️</div>
                  <span>Location</span>
                </div>
                <div className="service-item">
                  <div className="service-icon">👷</div>
                  <span>Conducteur</span>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="mobile-ad">
          <div className="ad-content">
            <div className="ad-header">
              <img 
                src={campaign?.avatar_url || '/avatars/default.png'} 
                alt={campaign?.business_name} 
                className="ad-avatar" 
              />
              <span>{campaign?.business_name || 'Nom du prestataire'}</span>
            </div>
            <div className="ad-body">
              <h4>{formData.short_title || campaign?.short_title || 'Titre de la publicité'}</h4>
              <p>{formData.short_description || campaign?.short_description || 'Description de la publicité'}</p>
              {(formData.visual_url || campaign?.visual_url) && (
                <div className="ad-image">
                  <img 
                    src={formData.visual_url || campaign?.visual_url} 
                    alt="Publicité" 
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
              <button className="ad-button">Consulter</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Vue Recherche (nouveau)
  const renderSearchPreview = () => (
    <div className="mobile-frame">
      <div className="mobile-header">
        <div className="mobile-status-bar">
          <div className="carrier">WAAGO</div>
          <div className="time">9:41</div>
          <div className="battery">100%</div>
        </div>
        <div className="mobile-search active">
          <input type="text" placeholder="Rechercher un prestataire..." value="plombier" readOnly />
          <div className="search-icon">🔍</div>
        </div>
      </div>
      
      <div className="mobile-content">
        <div className="search-results">
          {/* Premier résultat = Publicité */}
          <div className="search-result ad-result">
            <div className="result-header">
              <img 
                src={campaign?.avatar_url || '/avatars/default.png'} 
                alt={campaign?.business_name} 
                className="result-avatar" 
              />
              <div className="result-info">
                <div className="result-name">
                  {campaign?.business_name || 'Nom du prestataire'}
                  <span className="ad-badge">Pub</span>
                </div>
                <div className="result-title">{formData.short_title || campaign?.short_title || 'Titre de la publicité'}</div>
                <div className="result-description">{formData.short_description || campaign?.short_description || 'Description de la publicité'}</div>
              </div>
            </div>
            {(formData.visual_url || campaign?.visual_url) && (
              <div className="result-image">
                <img 
                  src={formData.visual_url || campaign?.visual_url} 
                  alt="Publicité" 
                />
              </div>
            )}
          </div>
          
          {/* Autres résultats normaux */}
          <div className="search-result">
            <div className="result-header">
              <img src="/avatars/plombier1.png" alt="Jean Plombier" className="result-avatar" />
              <div className="result-info">
                <div className="result-name">Jean Plombier</div>
                <div className="result-rating">⭐ 4.8 (127 avis)</div>
                <div className="result-distance">À 2.3 km</div>
              </div>
            </div>
          </div>
          
          <div className="search-result">
            <div className="result-header">
              <img src="/avatars/plombier2.png" alt="Marie Réparation" className="result-avatar" />
              <div className="result-info">
                <div className="result-name">Marie Réparation</div>
                <div className="result-rating">⭐ 4.6 (89 avis)</div>
                <div className="result-distance">À 3.1 km</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Vue Pop-up (nouveau)
  const renderPopupPreview = () => (
    <div className="mobile-frame">
      <div className="mobile-header">
        <div className="mobile-status-bar">
          <div className="carrier">WAAGO</div>
          <div className="time">9:41</div>
          <div className="battery">100%</div>
        </div>
        <div className="mobile-search">
          <input type="text" placeholder="Rechercher un prestataire..." />
        </div>
      </div>
      
      <div className="mobile-content">
        <div className="mobile-wallet">
          <div className="wallet-info">
            <span>Mon Porte-feuille</span>
            <span className="wallet-amount">26.887,15 CFA</span>
          </div>
          <div className="wallet-actions">
            <button>Recharger</button>
            <button>Transférer</button>
          </div>
        </div>

        <div className="mobile-services">
          <h3>Nos services</h3>
          <div className="service-grid">
            {serviceCategories && serviceCategories.length > 0 ? (
              serviceCategories.slice(0, 4).map((cat, index) => {
                const serviceName = cat.name || cat;
                return (
                  <div className="service-item" key={cat.id || index}>
                    <div className="service-icon">{serviceIcons[serviceName] || serviceIcons.default}</div>
                    <span>{serviceName}</span>
                  </div>
                );
              })
            ) : (
              <>
                <div className="service-item">
                  <div className="service-icon">🚗</div>
                  <span>Transport</span>
                </div>
                <div className="service-item">
                  <div className="service-icon">📦</div>
                  <span>Livraison</span>
                </div>
                <div className="service-item">
                  <div className="service-icon">🏗️</div>
                  <span>Location</span>
                </div>
                <div className="service-item">
                  <div className="service-icon">👷</div>
                  <span>Conducteur</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Popup Overlay */}
      <div className="popup-overlay">
        <div className="popup-modal">
          <button className="popup-close">✕</button>
          <div className="popup-content">
            <div className="popup-header">
              <img 
                src={campaign?.avatar_url || '/avatars/default.png'} 
                alt={campaign?.business_name} 
                className="popup-avatar" 
              />
              <span className="popup-title">{campaign?.business_name || 'Nom du prestataire'}</span>
            </div>
            <div className="popup-body">
              <h4>{formData.short_title || campaign?.short_title || 'Titre de la publicité'}</h4>
              <p>{formData.short_description || campaign?.short_description || 'Description de la publicité'}</p>
              {(formData.visual_url || campaign?.visual_url) && (
                <div className="popup-image">
                  <img 
                    src={formData.visual_url || campaign?.visual_url} 
                    alt="Publicité" 
                  />
                </div>
              )}
              <button className="popup-button">Consulter</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="publicite-detail-container">
      <div className="detail-header">
        <button className="back-button" onClick={handleBack}>
          <FiArrowLeft />
          Détail publicité & visibilité
        </button>
        <div className="header-actions">
          {isEditing ? (
            <>
              <button className="cancel-edit-btn" onClick={handleEditToggle}>
                <FiX />
                Annuler
              </button>
              <button 
                className="save-edit-btn" 
                onClick={handleSaveChanges}
                disabled={saving}
              >
                <FiSave />
                {saving ? 'Sauvegarde...' : 'Enregistrer'}
              </button>
            </>
          ) : (
            <>
              {getStatusActionButtons()}
              <button className="edit-btn" onClick={handleEditToggle}>
                <FiEdit2 />
                Modifier
              </button>
            </>
          )}
        </div>
      </div>

      <div className="detail-content">
        <div className="mobile-preview">
          {renderMobilePreviewByEmplacement()}
        </div>

        <div className="campaign-info">
          <div className="campaign-header">
            <div className="campaign-id">
              <span className="id-label"># {campaign.id}</span>
              {getStatusBadge(campaign.status)}
            </div>
          </div>

          <div className="campaign-details">

            {(campaign.status === 'suspendu' || campaign.status === 'rejeté') && campaign.reason && (
              <div className="rejection-reason">
                <h4>Commentaire de {campaign.status === 'suspendu' ? 'suspension' : 'rejet'}</h4>
                <p>{campaign.reason}</p>
              </div>
            )}

            {/* Section d'édition des nouveaux champs */}
            <div className="campaign-edit-section">
              <h3>Informations publicitaires</h3>
              
              {/* Dates et statut */}
              <div className="edit-row">
                <div className="edit-field">
                  <label>Date de début</label>
                  {isEditing ? (
                    <input
                      type="date"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleInputChange}
                      required
                    />
                  ) : (
                    <div className="display-value">
                      {campaign.start_date || <span className="no-value">Non définie</span>}
                    </div>
                  )}
                </div>
                
                <div className="edit-field">
                  <label>Date de fin</label>
                  {isEditing ? (
                    <input
                      type="date"
                      name="end_date"
                      value={formData.end_date}
                      onChange={handleInputChange}
                      required
                    />
                  ) : (
                    <div className="display-value">
                      {campaign.end_date || <span className="no-value">Non définie</span>}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="edit-row">
                <div className="edit-field">
                  <label>Emplacement</label>
                  {isEditing ? (
                    <select
                      name="emplacement"
                      value={formData.emplacement}
                      onChange={handleInputChange}
                    >
                      <option value="Accueil">Accueil</option>
                      <option value="Recherche">Recherche</option>
                      <option value="Pop up">Pop up</option>
                    </select>
                  ) : (
                    <div className="display-value">
                      {campaign.emplacement || <span className="no-value">Non défini</span>}
                    </div>
                  )}
                </div>
                
                <div className="edit-field">
                  <label>Statut de la publicité</label>
                  {isEditing ? (
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                    >
                      <option value="en_attente">En attente</option>
                      <option value="en_cours">En cours</option>
                      <option value="suspendu">Suspendu</option>
                      <option value="rejeté">Rejeté</option>
                      <option value="accepté">Accepté</option>
                      <option value="terminé">Terminé</option>
                    </select>
                  ) : (
                    <div className="display-value">
                      {getStatusBadge(campaign.status)}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="edit-field">
                <label>Visuel publicitaire</label>
                {isEditing ? (
                  <div className="file-upload-container">
                    <input
                      type="file"
                      id="visual_upload"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="file-input"
                    />
                    <label htmlFor="visual_upload" className="file-upload-button">
                      <FiUpload />
                      Choisir un fichier
                    </label>
                    {formData.visual_url && (
                      <div className="uploaded-file">
                        <img 
                          src={formData.visual_url} 
                          alt="Aperçu" 
                          className="visual-preview"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const nextElement = e.target.nextElementSibling;
                            if (nextElement) {
                              nextElement.style.display = 'block';
                            }
                          }}
                        />
                        <span className="file-name" style={{display: 'none'}}>
                          {formData.visual_url.split('/').pop()}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="display-value">
                    {campaign.visual_url ? (
                      <img 
                        src={campaign.visual_url} 
                        alt="Visuel" 
                        className="visual-preview"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <span className="no-value">Aucun visuel</span>
                    )}
                  </div>
                )}
              </div>

              <div className="edit-field">
                <label>Titre court</label>
                {isEditing ? (
                  <input
                    type="text"
                    name="short_title"
                    value={formData.short_title}
                    onChange={handleInputChange}
                    placeholder="Ex: 30% de réduction sur les machines"
                    maxLength="100"
                  />
                ) : (
                  <div className="display-value">
                    {campaign.short_title || <span className="no-value">Non défini</span>}
                  </div>
                )}
              </div>

              <div className="edit-field">
                <label>Description courte</label>
                {isEditing ? (
                  <textarea
                    name="short_description"
                    value={formData.short_description}
                    onChange={handleInputChange}
                    placeholder="Description de la publicité..."
                    rows="3"
                    maxLength="200"
                  />
                ) : (
                  <div className="display-value">
                    {campaign.short_description || <span className="no-value">Non définie</span>}
                  </div>
                )}
              </div>

              <div className="edit-row">
                <div className="edit-field">
                  <label>Montant à payer</label>
                  {isEditing ? (
                    <div className="amount-input">
                      <input
                        type="number"
                        name="amount_to_pay"
                        value={formData.amount_to_pay}
                        onChange={handleInputChange}
                        placeholder="6000"
                        step="0.01"
                        min="0"
                      />
                      <span className="currency">FCFA</span>
                    </div>
                  ) : (
                    <div className="display-value">
                      {campaign.amount_to_pay ? 
                        `${campaign.amount_to_pay} FCFA` : 
                        <span className="no-value">Non défini</span>
                      }
                    </div>
                  )}
                </div>

                <div className="edit-field">
                  <label>Statut paiement</label>
                  {isEditing ? (
                    <select
                      name="payment_status"
                      value={formData.payment_status}
                      onChange={handleInputChange}
                    >
                      <option value="pending">En attente</option>
                      <option value="paid">Payé</option>
                      <option value="failed">Échec</option>
                    </select>
                  ) : (
                    <div className="display-value">
                      <span className={`payment-status status-${campaign.payment_status}`}>
                        {campaign.payment_status === 'pending' && 'En attente'}
                        {campaign.payment_status === 'paid' && 'Payé'}
                        {campaign.payment_status === 'failed' && 'Échec'}
                        {!campaign.payment_status && <span className="no-value">Non défini</span>}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="campaign-actions">
              {getActionButtons()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PubliciteDetail;